import { useEffect, useState } from "react";

import "react-toastify/dist/ReactToastify.css";
import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faEye } from "@fortawesome/free-solid-svg-icons";
import { currencyMask, displayDateTime } from "../../../utils/masks";
import api from "../../../services/api";
import { TablePage } from "../../../containers";
import { Link, useParams } from "react-router-dom";
import { OrderEventType } from "../../../models/enums/OrderEventType";
import { toast } from "react-toastify";
import toastOptions from "../../../utils/toastOptions";
import CongregationSelect from "../../../components/congregation-select";

interface OrderListDTO {
  id: number;
  keyOrder: string;
  createdDate: Date;
  createdBy: string;
  name: string;
  congregation: any;
  currentStatus?: any;
  amountDue: number;
  amountPaid?: number;
}

type RouteParams = {
  type: string;
}

export default function OrderList() {
  const params = useParams<RouteParams>();
  const [refresh, setRefresh] = useState<boolean>();
  const [total, setTotal] = useState(0);
  const [termSearch, setTermSearch] = useState<string>("");
  const [delayedSearchTerm, setDelayedSearchTerm] = useState('');
  const [pageNo, setPageNo] = useState<number>(1);
  const [cancelled, setCancelled] = useState<boolean>(false);
  const [idCongregation, setIdCongregation] = useState<number>(0);

  const columns = [
    {
      name: "Pedido",
      sortable: true,
      grow: 2,
      cell: (row: OrderListDTO) => <div className="uk-flex uk-flex-column uk-flex-left uk-margin-xsmall-y">
        <span>{row.name} - Nº {row.keyOrder}</span>
        {!row.amountPaid && <span className="uk-text-meta uk-text-italic uk-text-bold">Total: {currencyMask(row.amountDue)}</span>}
        {row.currentStatus && (<span className="uk-text-meta uk-text-italic">{row.currentStatus.note}</span>)}        
        <div>
          {!row.currentStatus && <span className="uk-badge">Recebido</span>}
          {row.currentStatus && (<>
            <span className={`uk-badge uk-badge-${handleOrderEventColor(row.currentStatus.type)} uk-text-nowrap`}>
              {row.currentStatus.type}
            </span>
          </>)}
          {row.currentStatus != OrderEventType.PAID && (row.amountPaid ?? 0) > 0 && (<>
            <span className="uk-badge uk-badge-success uk-margin-small-left">Pago</span>
          </>)}
        </div>
      </div>
    },
    {
      name: "Congregação",
      sortable: true,
      selector: 'congregation.description',
      omit: idCongregation > 0,
      cell: (row: OrderListDTO) => <span className="uk-text-nowrap">{row.congregation?.description}</span>
    },
    {
      name: "Criado Em",
      sortable: true,
      cell: (row: OrderListDTO) => <span>{displayDateTime(row.createdDate)}</span>
    },
    {
      name: 'Ações',
      button: true,
      sortable: false,
      cell: (row: OrderListDTO) => (
        <div className='uk-flex uk-flex-between'>
          <a href={`/pedidos/${params.type}/${row.keyOrder}`} uk-tooltip="title: Visualizar" 
            className='uk-margin-small-right'
            target="_blank"
          >
            <FontAwesomeIcon icon={faEye} />
          </a>
        </div>
      )
    }
  ];

  useEffect(() => {
    const delaySearch = setTimeout(() => {
      setDelayedSearchTerm(termSearch);
    }, 500); // You can adjust the delay time according to your preferences

    return () => clearTimeout(delaySearch);
  }, [termSearch]);

  useEffect(() => {
    // Perform the search operation with delayedSearchTerm
    // This will be called only when the user stops typing for the specified delay time
    setRefresh(!refresh);
  }, [delayedSearchTerm]);

  const fetchOrders = async (page: number, limit: number) => {
    try {
      setTotal(0);

      let orderParams = {
        page: page,
        limit: limit,
        type: params.type,
        cancelled: cancelled,
        congregation: idCongregation
      } as any;

      if (termSearch) {
        orderParams = { ...orderParams, name: termSearch?.toUpperCase() };
      }

      const response = await api.get(`orders/${params.type}/list`, {
        params: orderParams
      });

      setTotal(response.data.total);

      setPageNo(page);

      return response;
    } catch (e: any) {
      e.messages?.forEach((m: string) => {
        toast.error(m, toastOptions);
      });
      return { data: [], total: 0 };
    }
  };

  const handleSearch = async (search: string) => {
    if (search.length > 2 || search.length === 0) {
      setTermSearch(search);
      setRefresh(!refresh);
    }
  };

  const handleCancelled = async(selected: boolean) => {
    setCancelled(selected);
    setRefresh(!refresh);
  };

  const handleCongregation = async(id: number) => {
    setIdCongregation(id);
    setRefresh(!refresh);
  };

  const handleOptions = () => {
    return (
      <div className="uk-grid-small" uk-grid="true">
        <div className="uk-search uk-search-default uk-width-1-3@m">
          <span
            className="uk-search-icon-flip"
            uk-search-icon="true"
          ></span>
          <input
            className="uk-search-input uk-input uk-form-small"
            type="search"
            placeholder="Pesquise por nome ou número do pedido"
            style={{ textTransform: "uppercase" }}
            onChange={(ev) => {
              handleSearch(ev.target.value);
            }}
          />
        </div>
        <div className="uk-width-1-3@m uk-flex uk-flex-center">
          <CongregationSelect afterSelect={(id: number)=> handleCongregation(id)}/>
        </div>
        <div className="uk-width-1-3@m">
          <label className="uk-form-label uk-margin-small-right" htmlFor="type">Cancelados?</label>
            <input className="uk-checkbox uk-margin-left" 
              type="checkbox"    
              id="cancelled"
              checked={cancelled}
              onChange={(e) => {
                handleCancelled(e.target.checked);
              }}/>
        </div>
      </div>
    )
  };
  
  const handleOrderEventColor = (type: string): string => {
    const colorMap: { [key: string]: string } = {
      [OrderEventType.PAID.toLowerCase()] : 'success',
      [OrderEventType.CANCELED.toLowerCase()] : 'danger',
      [OrderEventType.DELIVERED.toLowerCase()] : 'alternative',
      [OrderEventType.IN_PRODUCTION.toLowerCase()] : 'warning'
    };
    
    return colorMap[type?.toLowerCase()] || 'secondary';
  };

  return (<div id="page-order-list">
    <TablePage 
      title={params.type?.toUpperCase() + " • Pedidos Realizados"}
      columns={columns}
      fetch={fetchOrders}
      refresh={refresh}
      showSubHeader
      totalRecords={total}
      labelTotalRecords={`${total} ${total === 1 ? "Pedido" : "Pedidos"}`}
      options={handleOptions()}
      backUrl={`/${params.type}`}
      messageEmpty={<span>Nenhum <b>pedido</b> encontrado!</span>}
      pageNo={pageNo}
      extraHeaderButtons={<>
        <Link 
            to={`/pedidos/${params.type}/resumo`}
            className="uk-button uk-button-primary uk-button-small"
            style={{ background: 'linear-gradient(135deg, #667eea 0%, #764ba2 100%)', border: 'none' }}
          >
            Resumo
          </Link>
      </>}
    />
  </div>);
}
